import React, { Suspense } from 'react';
import { Loader2 } from 'lucide-react';

// Loading component
const LoadingSpinner = ({ message = 'Loading...' }) => (
  <div className="flex items-center justify-center p-8">
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-2" />
      <p className="text-gray-600 text-sm">{message}</p>
    </div>
  </div>
);

// Higher-order component for lazy loading with custom loading states
export const withLazyLoading = (LazyComponent, loadingMessage) => {
  return (props) => (
    <Suspense fallback={<LoadingSpinner message={loadingMessage} />}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

// Lazy load heavy components
export const LazyBudgetSection = React.lazy(() => 
  import('../BudgetSection').then(module => ({ default: module.default }))
);

export const LazyPricingCalculator = React.lazy(() => 
  import('../PricingCalculator').then(module => ({ default: module.default }))
);

export const LazyTaxCalculation = React.lazy(() => 
  import('../TaxCalculation').then(module => ({ default: module.default }))
);

export const LazyReceiptScannerIntegration = React.lazy(() => 
  import('../components/ReceiptScanner/ReceiptScannerIntegration').then(module => ({ default: module.default }))
);

export const LazyVoiceInput = React.lazy(() => 
  import('../components/VoiceInput').then(module => ({ default: module.default }))
);

export const LazyExportCSV = React.lazy(() => 
  import('../ExportCSV').then(module => ({ default: module.default }))
);

export const LazyReportExport = React.lazy(() => 
  import('../ReportExport').then(module => ({ default: module.default }))
);

// Wrapped components with loading states
export const BudgetSection = withLazyLoading(LazyBudgetSection, 'Loading budget tools...');
export const PricingCalculator = withLazyLoading(LazyPricingCalculator, 'Loading pricing calculator...');
export const TaxCalculation = withLazyLoading(LazyTaxCalculation, 'Loading tax calculations...');
export const ReceiptScannerIntegration = withLazyLoading(LazyReceiptScannerIntegration, 'Loading receipt scanner...');
export const VoiceInput = withLazyLoading(LazyVoiceInput, 'Loading voice input...');
export const ExportCSV = withLazyLoading(LazyExportCSV, 'Preparing export...');
export const ReportExport = withLazyLoading(LazyReportExport, 'Loading reports...');

// Preload functions for better UX
export const preloadComponent = (componentImport) => {
  // Preload on hover or user interaction
  return componentImport();
};

export const preloadBudgetSection = () => preloadComponent(() => import('../BudgetSection'));
export const preloadPricingCalculator = () => preloadComponent(() => import('../PricingCalculator'));
export const preloadTaxCalculation = () => preloadComponent(() => import('../TaxCalculation'));
export const preloadReceiptScanner = () => preloadComponent(() => import('../components/ReceiptScanner/ReceiptScannerIntegration'));
export const preloadVoiceInput = () => preloadComponent(() => import('../components/VoiceInput'));

// Component preloader utility
export class ComponentPreloader {
  static preloadedComponents = new Set();
  
  static preload(componentName, importFunction) {
    if (!this.preloadedComponents.has(componentName)) {
      this.preloadedComponents.add(componentName);
      importFunction().catch(error => {
        console.warn(`Failed to preload ${componentName}:`, error);
        this.preloadedComponents.delete(componentName);
      });
    }
  }
  
  static preloadOnHover(componentName, importFunction) {
    return {
      onMouseEnter: () => this.preload(componentName, importFunction),
      onFocus: () => this.preload(componentName, importFunction)
    };
  }
}

// Performance monitoring for lazy components
export const withPerformanceMonitoring = (Component, componentName) => {
  return React.forwardRef((props, ref) => {
    React.useEffect(() => {
      const startTime = performance.now();
      
      return () => {
        const endTime = performance.now();
        const loadTime = endTime - startTime;
        
        // Log performance metrics
        if (loadTime > 1000) { // Log if component takes more than 1 second to render
          console.warn(`${componentName} took ${loadTime.toFixed(2)}ms to render`);
        }
        
        // Send to analytics if needed
        if (window.gtag) {
          window.gtag('event', 'component_load_time', {
            component_name: componentName,
            load_time: Math.round(loadTime),
            custom_parameter: 'lazy_component'
          });
        }
      };
    }, []);
    
    return <Component ref={ref} {...props} />;
  });
};

// Resource hints for better loading
export const addResourceHints = () => {
  // Add preload hints for critical resources
  const preloadHints = [
    { href: '/static/css/main.css', as: 'style' },
    { href: '/static/js/main.js', as: 'script' },
    { href: '/logo.png.png', as: 'image' }
  ];
  
  preloadHints.forEach(hint => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = hint.href;
    link.as = hint.as;
    
    if (hint.as === 'style') {
      link.onload = () => {
        link.rel = 'stylesheet';
      };
    }
    
    document.head.appendChild(link);
  });
  
  // Add prefetch hints for likely next resources
  const prefetchHints = [
    '/static/js/budget.chunk.js',
    '/static/js/pricing.chunk.js',
    '/static/js/receipt.chunk.js'
  ];
  
  prefetchHints.forEach(href => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    document.head.appendChild(link);
  });
};

// Initialize performance optimizations
export const initializePerformanceOptimizations = () => {
  // Add resource hints
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addResourceHints);
  } else {
    addResourceHints();
  }
  
  // Preload critical components after initial render
  setTimeout(() => {
    // Preload components that are likely to be used soon
    preloadBudgetSection();
    preloadPricingCalculator();
  }, 2000);
  
  // Preload components based on user behavior
  let userInteractionCount = 0;
  const handleUserInteraction = () => {
    userInteractionCount++;
    
    if (userInteractionCount === 3) {
      // User is engaged, preload more components
      preloadReceiptScanner();
      preloadVoiceInput();
    }
    
    if (userInteractionCount === 5) {
      // User is very engaged, preload remaining components
      preloadTaxCalculation();
    }
  };
  
  ['click', 'touchstart', 'keydown'].forEach(event => {
    document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
  });
};

export default {
  BudgetSection,
  PricingCalculator,
  TaxCalculation,
  ReceiptScannerIntegration,
  VoiceInput,
  ExportCSV,
  ReportExport,
  ComponentPreloader,
  initializePerformanceOptimizations
};